import React, { useState } from 'react'

export default function ContactForm({ defaultService = '', className = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: defaultService,
    description: '',
    website_url: '' // mandatory honeypot
  })

  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  const validate = () => {
    const errors = {}
    if (!formData.name.trim()) {
      errors.name = 'Angiv venligst dit navn'
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Angiv venligst dit telefonnummer'
    } else if (formData.phone.replace(/[\s+-]/g, '').length < 8) {
      errors.phone = 'Angiv et gyldigt telefonnummer (mindst 8 cifre)'
    }
    if (!formData.email.trim()) {
      errors.email = 'Angiv venligst din e-mail'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Angiv en gyldig e-mailadresse'
    }
    if (!formData.description.trim()) {
      errors.description = 'Beskriv venligst kort opgaven'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validate()) return

    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          service: formData.service || 'Ikke angivet',
          description: formData.description.trim(),
          website_url: formData.website_url // honeypot
        })
      })

      const result = await response.json().catch(() => ({}))

      if (response.ok) {
        setStatus('success')
      } else {
        setStatus('error')
        setErrorMessage(
          result.message ||
            'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte på 31 34 16 32.'
        )
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(
        'Kunne ikke oprette forbindelse. Prøv venligst igen eller kontakt os direkte på 31 34 16 32.'
      )
    }
  }

  const handleReset = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      description: '',
      website_url: ''
    })
    setStatus('idle')
    setFieldErrors({})
  }

  if (status === 'success') {
    return (
      <div className={`rounded-3xl border border-emerald-100 bg-emerald-50/70 p-8 text-center sm:p-10 shadow-lg ${className}`}>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#0d3835] text-white shadow-sm mb-4">
          <i className="fa-regular fa-check text-2xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display">
          Tak for din henvendelse!
        </h3>
        <p className="mt-3 text-slate-700 leading-relaxed max-w-md mx-auto text-sm sm:text-base">
          Vi har modtaget din besked og kontakter dig hurtigst muligt for en uforpligtende snak og et tilbud tilpasset din opgave.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="tel:31341632"
            className="inline-flex items-center gap-2 rounded-xl bg-[#f05a28] px-5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-[#db4d1d] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f05a28] cursor-pointer"
          >
            <i className="fa-regular fa-phone text-xs" aria-hidden="true" />
            <span className="no-break">Ring direkte: 31 34 16 32</span>
          </a>
          <button
            type="button"
            onClick={handleReset}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors cursor-pointer underline underline-offset-4"
          >
            Send en ny besked
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={`relative rounded-3xl border border-slate-200/90 bg-white p-6 sm:p-8 lg:p-10 shadow-xl shadow-slate-200/50 ${className}`}
    >
      {/* Honeypot field for bot protection (sr-only absolute as strictly required) */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="form_website_url">Website URL (lad dette felt stå tomt)</label>
        <input
          id="form_website_url"
          name="website_url"
          type="text"
          value={formData.website_url}
          onChange={e => setFormData({ ...formData, website_url: e.target.value })}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-[#f05a28] mb-2">
          Uforpligtende tilbud
        </div>
        <h3 className="text-2xl font-bold tracking-tight text-slate-900 font-display">
          Få et uforpligtende tilbud
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Udfyld formularen herunder, så vender vi hurtigt tilbage med en snak om din opgave.
        </p>
      </div>

      {status === 'error' && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="flex items-center gap-2 font-semibold">
            <i className="fa-regular fa-triangle-exclamation" aria-hidden="true" />
            Der opstod en fejl
          </div>
          <p className="mt-1 text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="Dit fulde navn"
            value={formData.name}
            onChange={e => {
              setFormData({ ...formData, name: e.target.value })
              if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: '' })
            }}
            className={`w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-colors ${
              fieldErrors.name
                ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                : 'border-slate-300 focus:border-[#0d3835] focus:ring-2 focus:ring-[#0d3835]/15'
            }`}
          />
          {fieldErrors.name && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
          )}
        </div>

        {/* Two-column on tablets/desktops: Phone & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
              Telefonnummer <span className="text-[#f05a28]">*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              placeholder="F.eks. 31 34 16 32"
              value={formData.phone}
              onChange={e => {
                setFormData({ ...formData, phone: e.target.value })
                if (fieldErrors.phone) setFieldErrors({ ...fieldErrors, phone: '' })
              }}
              className={`w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-colors ${
                fieldErrors.phone
                  ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                  : 'border-slate-300 focus:border-[#0d3835] focus:ring-2 focus:ring-[#0d3835]/15'
              }`}
            />
            {fieldErrors.phone && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
              E-mail <span className="text-[#f05a28]">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="din@email.dk"
              value={formData.email}
              onChange={e => {
                setFormData({ ...formData, email: e.target.value })
                if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: '' })
              }}
              className={`w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-colors ${
                fieldErrors.email
                  ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                  : 'border-slate-300 focus:border-[#0d3835] focus:ring-2 focus:ring-[#0d3835]/15'
              }`}
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
            )}
          </div>
        </div>

        {/* Service type selector */}
        <div>
          <label htmlFor="service" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
            Ønsket ydelse
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={e => setFormData({ ...formData, service: e.target.value })}
            className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#0d3835] focus:outline-none focus:ring-2 focus:ring-[#0d3835]/15 transition-colors"
          >
            <option value="">Vælg eventuelt ydelse...</option>
            <option value="Privat rengøring">Privat rengøring</option>
            <option value="Erhvervsrengøring">Erhvervsrengøring</option>
            <option value="Hovedrengøring">Hovedrengøring</option>
            <option value="Flytterengøring">Flytterengøring</option>
            <option value="Klinik rengøring">Klinik rengøring (særlig erfaring)</option>
            <option value="Anden opgave">Anden rengøringsopgave</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
            Fortæl kort om den rengøring, du ønsker hjælp til <span className="text-[#f05a28]">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            rows={4}
            required
            placeholder="F.eks. boligens eller lokalernes størrelse, særlige ønsker, eller hvor ofte du har brug for rengøring..."
            value={formData.description}
            onChange={e => {
              setFormData({ ...formData, description: e.target.value })
              if (fieldErrors.description) setFieldErrors({ ...fieldErrors, description: '' })
            }}
            className={`w-full rounded-xl border px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-colors ${
              fieldErrors.description
                ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                : 'border-slate-300 focus:border-[#0d3835] focus:ring-2 focus:ring-[#0d3835]/15'
            }`}
          />
          {fieldErrors.description && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.description}</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#f05a28] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#db4d1d] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f05a28] disabled:opacity-50 transition-colors cursor-pointer"
        >
          {status === 'submitting' ? (
            <>
              <i className="fa-regular fa-spinner-third fa-spin text-sm" aria-hidden="true" />
              <span>Sender din besked...</span>
            </>
          ) : (
            <>
              <span>Send henvendelse</span>
              <i className="fa-regular fa-paper-plane text-xs" aria-hidden="true" />
            </>
          )}
        </button>

        <p className="text-xs text-slate-500">
          Uforpligtende tilbud · Svar hurtigst muligt
        </p>
      </div>
    </form>
  )
}
